import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Html5Qrcode } from "html5-qrcode";
import { lookupAttendeeByQR } from "../services/api";
import { useParams } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";

import {
  getEventUsers,
  addEventUser,
  updateEventUser,
  deleteEventUser,
  getAllEvents,
  copyUsersToEvent,
  lookupAttendeeForEvent,
  getTicketTypes,
  approveEventUser,
} from "../services/api";
import api from "../services/api";

// import { Html5Qrcode } from 'html5-qrcode';

import {
  PlusIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  QrCodeIcon,
  XMarkIcon,
  UserGroupIcon,
  CameraIcon,
} from "@heroicons/react/24/outline";

import { QRCodeCanvas } from "qrcode.react";

import { useSocket } from "../context/SocketContext";
import useToast from "../hooks/useToast";
const Users = () => {
  const { user: currentUser } = useAuth();
  const socket = useSocket();
  const { showSuccess, showError } = useToast();
  const { eventId } = useParams();

  // =====================================================
  // MODALS
  // =====================================================

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [showCopyModal, setShowCopyModal] = useState(false);

  // =====================================================
  // QR SCANNER
  // =====================================================

  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState("");

  const scannerRef = useRef(null);

  const [selectedSourceEventId, setSelectedSourceEventId] = useState("");

  // =====================================================
  // FORM
  // =====================================================

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    status: "active",
    customId: "",
    userId: "",
    ticketTypeId: "",
  });

  const [customData, setCustomData] = useState({});

  // =====================================================
  // GET EVENT USERS
  // =====================================================

  const {
    data: users = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery(
    ["event-users", eventId],
    () => getEventUsers(eventId).then((res) => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,

      onError: () => {
        alert("Failed to fetch users. Check console.");
      },
    },
  );

  // Event info (to know if this is a QR event or NFC event)
  const { data: eventInfo } = useApiQuery(
    ["event-info", eventId],
    () => api.get(`/events/${eventId}`).then((res) => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
    },
  );

  const isQREvent = eventInfo?.eventType === "qr";

  // =====================================================
  // CUSTOM FIELDS
  // =====================================================

  const { data: customFields = [], refetch: refetchCustomFields } = useApiQuery(
    ["custom-fields"],
    () => api.get("/custom-fields").then((res) => res.data),

    {
      staleTime: 5 * 60 * 1000,

      onError: () => {
        console.error("Failed to load custom fields");
      },
    },
  );
  // =====================================================
  // TICKET TYPES (event-scoped)
  // =====================================================

  const { data: ticketTypes = [], refetch: refetchTicketTypes } = useApiQuery(
    ["ticket-types", eventId],
    () => getTicketTypes(eventId).then((res) => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
      onError: () => {
        console.error("Failed to load ticket types");
      },
    },
  );
  // =====================================================
  // ALL EVENTS
  // =====================================================

  const { data: allEvents = [] } = useApiQuery(
    ["all-events"],

    async () => {
      const res = await getAllEvents();

      return Array.isArray(res.data) ? res.data : res.data?.data || [];
    },

    {
      staleTime: 5 * 60 * 1000,

      onError: () => {
        console.error("Failed to load events");
      },
    },
  );

  const copyableEvents = useMemo(() => {
    return Array.isArray(allEvents)
      ? allEvents.filter((event) => event._id !== eventId)
      : [];
  }, [allEvents, eventId]);

  // =====================================================
  // ADD EVENT USER
  // =====================================================

  const addUserMutation = useApiMutation(
    (data) => addEventUser(eventId, data).then((res) => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
        handleCloseModal();
      },

      onError: (error) => {
        console.error("Error saving user:", error);

        alert(
          "Error saving user: " +
            (error.response?.data?.message || "Unknown error"),
        );
      },
    },
  );

  // =====================================================
  // UPDATE EVENT USER
  // =====================================================

  const updateUserMutation = useApiMutation(
    ({ userId, data }) =>
      updateEventUser(eventId, userId, data).then((res) => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
        handleCloseModal();
      },

      onError: (error) => {
        alert(
          "Error saving user: " +
            (error.response?.data?.message || "Unknown error"),
        );
      },
    },
  );

  // =====================================================
  // DELETE EVENT USER
  // =====================================================

  const deleteUserMutation = useApiMutation(
    (userId) => deleteEventUser(eventId, userId).then((res) => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
        showSuccess("User deleted successfully");
      },

      onError: (error) => {
        alert(
          "Error deleting user: " +
            (error.response?.data?.message || "Unknown error"),
        );
      },
    },
  );

  const approveUserMutation = useApiMutation(
    ({ userId, approved }) =>
      approveEventUser(eventId, userId, approved).then((res) => res.data),
    {
      onSuccess: async (data) => {
        await refetchUsers();
        showSuccess(data?.message || "Approval updated");
      },
      onError: (error) => {
        showError(error.response?.data?.message || "Failed to update approval");
      },
    },
  );

  // =====================================================
  // COPY USERS
  // =====================================================

  const copyUsersMutation = useApiMutation(
    ({ targetEventId, sourceEventId }) =>
      copyUsersToEvent(targetEventId, sourceEventId).then((res) => res.data),

    {
      onSuccess: async () => {
        alert("Users copied successfully");

        setShowCopyModal(false);

        await refetchUsers();
      },

      onError: () => {
        alert("Failed to copy users");
      },
    },
  );

  // =====================================================
  // SOCKET
  // =====================================================

  useEffect(() => {
    if (!socket) return;

    const handleRefresh = () => {
      refetchUsers();
    };

    const handleTicketTypesChanged = () => {
      refetchTicketTypes();
      refetchUsers();
    };

    socket.on("eventUserCreated", handleRefresh);

    socket.on("eventUserUpdated", handleRefresh);

    socket.on("eventUserDeleted", handleRefresh);

    socket.on("eventUsersCopied", handleRefresh);

    socket.on("customFieldsChanged", refetchCustomFields);

    socket.on("eventTicketTypesChanged", handleTicketTypesChanged);

    return () => {
      socket.off("eventUserCreated", handleRefresh);

      socket.off("eventUserUpdated", handleRefresh);

      socket.off("eventUserDeleted", handleRefresh);

      socket.off("eventUsersCopied", handleRefresh);

      socket.off("customFieldsChanged", refetchCustomFields);

      socket.off("eventTicketTypesChanged", handleTicketTypesChanged);
    };
  }, [socket, refetchUsers, refetchCustomFields, refetchTicketTypes]);
  // =====================================================
  // QR MODAL
  // =====================================================

  const openQRModal = useCallback((user) => {
    setSelectedUser(user);
    setShowQRModal(true);
  }, []);

  const closeQRModal = useCallback(() => {
    setShowQRModal(false);
    setSelectedUser(null);
  }, []);

  // =====================================================
  // STOP QR SCANNER
  // =====================================================
  // =====================================================
  // STOP QR SCANNER
  // =====================================================

  const stopScanner = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      await scannerRef.current.stop();
    } catch (error) {
      console.log("Scanner stop:", error);
    }
    try {
      await scannerRef.current.clear();
    } catch (error) {
      console.log("Scanner clear:", error);
    }
    scannerRef.current = null;
  }, []);

  // =====================================================
  // CLOSE ADD/EDIT MODAL
  // =====================================================

  const handleCloseModal = useCallback(async () => {
    await stopScanner();

    setShowModal(false);
    setEditingUser(null);

    setShowScanner(false);
    setScannerError("");

    setFormData({
      name: "",
      email: "",
      password: "",
      role: "user",
      status: "active",
      customId: "",
      userId: "",
      ticketTypeId: "",
    });

    setCustomData({});
  }, [stopScanner]);

  // =====================================================
  // QR SCAN RESULT
  // =====================================================
  const handleScanResult = useCallback(
    async (decodedText) => {
      try {
        await stopScanner();
        setShowScanner(false);
        setScannerError("");

        const customId = String(decodedText).trim();
        if (!customId) {
          setScannerError("Invalid QR code");
          return;
        }

        // Look up User by the 25-char customId
        const res = await lookupAttendeeByQR(eventId, customId);
        const attendee = res.data;

        // Auto-fill the form
        setFormData((prev) => ({
          ...prev,
          userId: attendee.userId,
          customId: attendee.customId,
          name: attendee.name || "",
          email: attendee.email || "",
          password: "",
          status: "active",
          role: "user",
        }));

        setShowModal(true);
      } catch (error) {
        console.error("QR lookup error:", error);
        setScannerError(
          error.response?.data?.message || "Unable to find attendee.",
        );
        setShowScanner(true);
      }
    },
    [eventId, stopScanner],
  );
  // =====================================================
  // START QR SCANNER
  // =====================================================

  // =====================================================
  // START QR SCANNER
  // =====================================================

  const startScanner = useCallback(async () => {
    try {
      setScannerError("");
      setShowScanner(true);

      // Wait for DOM
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (scannerRef.current) return;

      const scanner = new Html5Qrcode("attendee-qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        (decodedText) => {
          handleScanResult(decodedText);
        },
        () => {
          // QR not found. Ignore continuous scanner messages.
        },
      );
    } catch (error) {
      console.error("Unable to start camera:", error);
      scannerRef.current = null;
      setScannerError(
        "Unable to access camera. Please allow camera permission and try again.",
      );
    }
  }, [handleScanResult]);

  // =====================================================
  // COPY USERS
  // =====================================================

  const handleCopyUsers = useCallback(() => {
    if (!selectedSourceEventId) {
      alert("Please select an event to copy from");

      return;
    }

    copyUsersMutation.mutate({
      targetEventId: eventId,
      sourceEventId: selectedSourceEventId,
    });
  }, [selectedSourceEventId, eventId, copyUsersMutation]);

  // =====================================================
  // SUBMIT FORM
  // =====================================================

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      const payload = {
        ...formData,
        customData,
      };

      // Existing attendee scanned through QR
      if (formData.userId) {
        // Never send password
        delete payload.password;
      }

      if (editingUser) {
        updateUserMutation.mutate({
          userId: editingUser._id,

          data: payload,
        });
      } else {
        addUserMutation.mutate(payload);
      }
    },
    [formData, customData, editingUser, addUserMutation, updateUserMutation],
  );

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete = useCallback(
    (id) => {
      if (window.confirm("Are you sure you want to delete this user?")) {
        deleteUserMutation.mutate(id);
      }
    },
    [deleteUserMutation],
  );

  // =====================================================
  // EDIT
  // =====================================================

  const handleEdit = useCallback((user) => {
    setEditingUser(user);

    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: "user",
      status: user.status || "active",
      customId: user.customId || "",
      userId: "",
      ticketTypeId: user.ticketTypeId || "",
    });

    setCustomData(user.customData || {});

    setShowModal(true);
  }, []);

  // =====================================================
  // CUSTOM FIELD RENDER
  // =====================================================

  const renderDynamicField = useCallback(
    (field) => {
      const value = customData[field.name] || "";

      const onChange = (e) => {
        setCustomData((prev) => ({
          ...prev,

          [field.name]: e.target.value,
        }));
      };

      const required = field.required;

      switch (field.type) {
        case "text":
        case "email":
          return (
            <input
              type={field.type}
              value={value}
              onChange={onChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              required={required}
            />
          );

        case "number":
          return (
            <input
              type="number"
              value={value}
              onChange={onChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              required={required}
            />
          );

        case "date":
          return (
            <input
              type="date"
              value={value}
              onChange={onChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              required={required}
            />
          );

        case "textarea":
          return (
            <textarea
              value={value}
              onChange={onChange}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              required={required}
            />
          );

        case "select":
          return (
            <select
              value={value}
              onChange={onChange}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              required={required}
            >
              <option value="">Select...</option>

              {field.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );

        default:
          return null;
      }
    },
    [customData],
  );

  // =====================================================
  // LOADING
  // =====================================================

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />

          <p className="text-gray-500 text-sm">Loading users...</p>
        </div>
      </div>
    );
  }

  const isSubmitting =
    addUserMutation.isPending || updateUserMutation.isPending;

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="space-y-6">
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage attendees for this event
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* COPY */}

          <button
            onClick={() => setShowCopyModal(true)}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
          >
            <ClipboardIcon className="w-4 h-4" />
            Copy from Event
          </button>

          {/* ADD */}

          <button
            onClick={() => {
              setEditingUser(null);

              setFormData({
                name: "",
                email: "",
                password: "",
                role: "user",
                status: "active",
                customId: "",
                userId: "",
                ticketTypeId: "",
              });

              setCustomData({});

              setShowModal(true);
            }}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* =================================================
          USERS TABLE
      ================================================= */}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5 text-gray-400" />

            <h2 className="text-base font-medium text-gray-900">
              Registered Users
            </h2>

            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {users.length} users
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  User ID
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Ticket Type
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Approval
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>

                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <p className="text-gray-500">No users found</p>

                    <p className="text-sm text-gray-400 mt-1">
                      Click "Add User" to get started
                    </p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.customId ? (
                        <span className="text-sm font-mono text-gray-900">
                          {user.customId}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          Pending ID
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{user.name}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.email}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.ticketTypeName ? (
                        (() => {
                          const t = ticketTypes.find(
                            (tt) => tt._id === user.ticketTypeId,
                          );
                          const color = t?.color || "#1471b9";

                          return (
                            <span
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${color}20`,
                                color: color,
                              }}
                            >
                              {user.ticketTypeName}
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === "active"
                            ? "bg-[#1672b9]/10 text-[#1672b9]"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const isThisUserPending =
                          approveUserMutation.isPending &&
                          approveUserMutation.variables?.userId === user._id;

                        const isApproved = user.approved !== false;

                        return (
                          <button
                            onClick={() =>
                              approveUserMutation.mutate({
                                userId: user._id,
                                approved: !isApproved,
                              })
                            }
                            disabled={approveUserMutation.isPending}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition disabled:opacity-50 ${
                              isApproved
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-red-100 text-red-800 border-red-200 hover:bg-red-200"
                            }`}
                            title={
                              isApproved
                                ? "Click to disapprove this user"
                                : "Click to approve this user"
                            }
                          >
                            {isThisUserPending ? (
                              <>
                                <svg
                                  className="w-3 h-3 animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  />
                                </svg>
                                Updating...
                              </>
                            ) : isApproved ? (
                              <>✓ Approved</>
                            ) : (
                              <>Approve</>
                            )}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(user)}
                        disabled={isSubmitting}
                        className="text-gray-400 hover:text-[#1672b9] transition-colors mr-3 disabled:opacity-50"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(user._id)}
                        disabled={deleteUserMutation.isPending}
                        className="text-gray-400 hover:text-gray-600 transition-colors mr-3 disabled:opacity-50"
                        title="Delete"
                      >
                        {deleteUserMutation.isPending &&
                        deleteUserMutation.variables === user._id ? (
                          <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>

                      <button
                        onClick={() => openQRModal(user)}
                        className="text-gray-400 hover:text-[#1672b9] transition-colors"
                        title="Show QR"
                      >
                        <QrCodeIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =================================================
          ADD / EDIT MODAL
      ================================================= */}

      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                {editingUser ? "Edit User" : "Add New User"}
              </h3>
              <button
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-[#1672b9]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* 1. Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter full name"
                  required
                  disabled={isSubmitting}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                />
              </div>

              {/* 2. User ID (single field) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User ID (Unique)
                </label>
                <input
                  type="text"
                  value={formData.customId}
                  onChange={(e) => {
                    setFormData({ ...formData, customId: e.target.value });
                    setScannerError("");
                  }}
                  onKeyDown={
                    isQREvent
                      ? async (e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();

                          const value = e.target.value.trim();
                          if (!value) return;

                          try {
                            const res = await lookupAttendeeByQR(
                              eventId,
                              value,
                            );
                            const attendee = res.data;

                            setFormData((prev) => ({
                              ...prev,
                              userId: attendee.userId,
                              customId: attendee.customId,
                              name: attendee.name || "",
                              email: attendee.email || "",
                              password: "",
                              status: "active",
                              role: "user",
                            }));

                            setScannerError("");
                          } catch (err) {
                            setScannerError(
                              err.response?.data?.message ||
                                "No attendee found for this QR",
                            );
                          }
                        }
                      : undefined
                  }
                  placeholder={
                    isQREvent
                      ? "Scan QR or enter ID, then press Enter"
                      : "Enter custom ID"
                  }
                  required
                  disabled={isSubmitting || Boolean(formData.userId)}
                  autoFocus
                  className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm ${
                    isQREvent ? "font-mono" : ""
                  } focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] ${
                    formData.userId
                      ? "bg-gray-50 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                />
                {isQREvent && scannerError && (
                  <div className="mt-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">
                    {scannerError}
                  </div>
                )}

                {/* {isQREvent && formData.userId && (
  <p className="text-xs text-gray-400 mt-1">
    ID from scanned QR — cannot be changed
  </p>
)} */}

                {!isQREvent && (
                  <p className="text-xs text-gray-400 mt-1">
                    Assign the attendee's NFC card ID here
                  </p>
                )}
              </div>

              {/* 3. Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="Email address"
                  required
                  disabled={isSubmitting || Boolean(formData.userId)}
                  className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] ${
                    formData.userId
                      ? "bg-gray-50 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>

              {/* 4. Password */}
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder={
                      formData.userId
                        ? "Not required for QR attendee"
                        : "Optional"
                    }
                    disabled={isSubmitting || Boolean(formData.userId)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {/* 5. Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  disabled={isSubmitting}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* 6. Ticket Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket Type
                </label>
                <select
                  value={formData.ticketTypeId}
                  onChange={(e) =>
                    setFormData({ ...formData, ticketTypeId: e.target.value })
                  }
                  disabled={isSubmitting}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">— No ticket type —</option>
                  {ticketTypes.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 7. Custom Fields */}
              {customFields.map((field) => (
                <div key={field._id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required && " *"}
                  </label>
                  {renderDynamicField(field)}
                </div>
              ))}

              {/* 8. Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.name.trim() ||
                    !formData.email.trim() ||
                    !formData.customId.trim()
                  }
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50"
                >
                  {isSubmitting
                    ? "Saving..."
                    : editingUser
                      ? "Update User"
                      : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =================================================
          COPY MODAL
      ================================================= */}

      {showCopyModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                Copy Users from Another Event
              </h3>

              <button
                onClick={() => setShowCopyModal(false)}
                className="text-gray-400 hover:text-[#1672b9]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Source Event
                </label>

                <select
                  value={selectedSourceEventId}
                  onChange={(e) => setSelectedSourceEventId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Choose an event</option>

                  {copyableEvents.map((event) => (
                    <option key={event._id} value={event._id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  Cancel
                </button>

                <button
                  onClick={handleCopyUsers}
                  disabled={
                    copyUsersMutation.isPending || !selectedSourceEventId
                  }
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {copyUsersMutation.isPending ? "Copying..." : "Copy Users"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================
          EVENT USER QR
      ================================================= */}

      {showQRModal && selectedUser && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full mx-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                QR Code for {selectedUser.name}
              </h3>

              <button
                onClick={closeQRModal}
                className="text-gray-400 hover:text-[#1672b9]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <QRCodeCanvas value={selectedUser.customId} size={200} />
              </div>

              <p className="text-center mt-4 text-sm text-gray-600">
                User ID:{" "}
                <span className="font-mono font-semibold text-gray-900">
                  {selectedUser.customId}
                </span>
              </p>
            </div>

            <div className="p-5 border-t border-gray-100 flex justify-end">
              <button
                onClick={closeQRModal}
                className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
